import { useRef, useState } from "react";
import { api } from "@/lib/api";
import Modal from "@/components/Modal";
import { Upload, Loader2, CheckCircle2, XCircle, MinusCircle, Download } from "lucide-react";
import { toast } from "sonner";

// Minimal header parse for the mapping UI — the backend does the robust parse.
function parseHeader(text) {
  return (parseCsv(text).header || []).map((h) => h.replace(/^"|"$/g, "").trim()).filter(Boolean);
}

// Auto-match a column header to a field. Prefers exact matches over substring
// matches so e.g. "Sales Person" maps to the name field, not the email field.
const norm = (s) => s.toLowerCase().replace(/[^a-z]/g, "");

function autoMap(columns, fields) {
  const m = {};
  const claimed = new Set();
  const score = (col, field) => {
    const cl = norm(col), fl = norm(field.key.replace("_email", "email").replace("_name", "name"));
    if (cl === fl) return 3;                       // exact
    if (cl.includes(fl) || fl.includes(cl)) return 1; // substring
    return 0;
  };
  // For each field, pick the best-scoring unclaimed column.
  for (const f of fields) {
    if (f.key === "_blank") continue;
    let best = null, bestScore = 0;
    for (const c of columns) {
      if (claimed.has(c)) continue;
      const s = score(c, f);
      if (s > bestScore) { best = c; bestScore = s; }
    }
    if (best) { m[f.key] = best; claimed.add(best); }
  }
  return m;
}

// Parse CSV into { header: string[], rows: {num, cells}[] } where `num` matches
// the backend's row numbering (header is row 1, first data row is row 2). Handles
// quoted fields with embedded commas/newlines like Python's csv module.
function parseCsv(text) {
  const records = [];
  let field = "", record = [], inQuotes = false;
  const pushField = () => { record.push(field); field = ""; };
  const pushRecord = () => { pushField(); records.push(record); record = []; };
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; }
        else inQuotes = false;
      } else field += ch;
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ",") {
      pushField();
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && text[i + 1] === "\n") i++;
      pushRecord();
    } else field += ch;
  }
  if (field !== "" || record.length) pushRecord();
  // Match Python's csv.DictReader row numbering: a truly blank line yields a
  // single empty field ([""]) and is skipped; a whitespace-only line is kept.
  const clean = records.filter((r) => !(r.length === 1 && r[0] === ""));
  const header = clean[0] || [];
  const rows = clean.slice(1).map((cells, i) => ({ num: i + 2, cells }));
  return { header, rows };
}

// Quote a CSV cell if it contains a comma, quote, or newline.
function csvCell(v) {
  const s = v == null ? "" : String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/**
 * Generic CSV importer. Configure per entity via props:
 *  - title / subtitle: modal heading for the upload step
 *  - endpoint: backend import path (e.g. "/customers/import")
 *  - fields: [{ key, label, required }] the columns can map to (`company` required)
 *  - entitySingular / entityPlural: wording in toasts (e.g. "customer" / "customers")
 *  - failedFileName: filename for the downloadable failed-rows CSV
 */
export default function ImportRecordsModal({
  onClose,
  onImported,
  title,
  subtitle,
  endpoint,
  fields,
  entitySingular = "record",
  entityPlural = "records",
  failedFileName = "failed_import.csv",
}) {
  const [csvText, setCsvText] = useState("");
  const [columns, setColumns] = useState([]);
  const [mapping, setMapping] = useState({});
  const [rowCount, setRowCount] = useState(0);
  const [importing, setImporting] = useState(false);
  const [report, setReport] = useState(null); // { imported, failed, results: [{row, company, status, reason}] }
  const fileRef = useRef(null);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || "");
      const cols = parseHeader(text);
      setCsvText(text);
      setColumns(cols);
      setMapping(autoMap(cols, fields));
      setRowCount(parseCsv(text).rows.length);
      setReport(null);
    };
    reader.readAsText(file);
  };

  const setField = (field, col) => setMapping((m) => ({ ...m, [field]: col || undefined }));

  const doImport = async () => {
    if (!mapping.company) { toast.error("Map the Company column first"); return; }
    setImporting(true);
    try {
      const { data } = await api.post(endpoint, { csv_text: csvText, mapping });
      const failed = data.failed ?? (data.results || []).filter((r) => r.status !== "imported").length;
      setReport({ imported: data.imported || 0, failed, results: data.results || [] });
      if (data.imported > 0) toast.success(`Imported ${data.imported} ${entitySingular}(s)${failed ? `, ${failed} not added` : ""}`);
      else toast.error(`No ${entityPlural} added — ${failed} row(s) had issues`);
      // Refresh the list behind the modal, but keep the modal open to show the report.
      onImported && onImported({ keepOpen: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  // Build a CSV of the rows that weren't added, preserving original columns and
  // appending an "Import Error" column so the user can fix and re-upload.
  const downloadFailed = () => {
    if (!report) return;
    const failedRows = report.results.filter((r) => r.status !== "imported");
    if (!failedRows.length) return;
    const { header, rows } = parseCsv(csvText);
    const byNum = new Map(rows.map((r) => [r.num, r.cells]));
    const width = header.length;
    const lines = [[...header, "Import Error"].map(csvCell).join(",")];
    for (const r of failedRows) {
      const cells = byNum.get(r.row) || [];
      const padded = Array.from({ length: width }, (_, i) => cells[i] ?? "");
      lines.push([...padded, r.reason || ""].map(csvCell).join(","));
    }
    const blob = new Blob([lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = failedFileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  if (report) {
    const statusMeta = {
      imported: { Icon: CheckCircle2, color: "var(--success, #15803d)", label: "Added" },
      failed: { Icon: XCircle, color: "var(--danger)", label: "Not added" },
      skipped: { Icon: MinusCircle, color: "var(--text-2)", label: "Skipped" },
    };
    return (
      <Modal title="Import Results" subtitle="What was added and what wasn't" onClose={onClose} size="lg">
        <div data-testid="import-report">
          <div className="flex gap-3 mb-4">
            <div className="card-ledger" style={{ flex: 1, padding: "12px 16px" }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: "var(--success, #15803d)" }} data-testid="report-imported">{report.imported}</div>
              <div style={{ fontSize: 12, color: "var(--text-2)" }}>Added</div>
            </div>
            <div className="card-ledger" style={{ flex: 1, padding: "12px 16px" }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: report.failed ? "var(--danger)" : "var(--text-2)" }} data-testid="report-failed">{report.failed}</div>
              <div style={{ fontSize: 12, color: "var(--text-2)" }}>Not added</div>
            </div>
          </div>

          <div className="card-ledger overflow-hidden" style={{ maxHeight: 340, overflowY: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "var(--surface-2)", position: "sticky", top: 0 }}>
                  {["Row", "Company", "Status", "Reason"].map((h) => (
                    <th key={h} className="overline" style={{ fontSize: 10, padding: "10px 14px", textAlign: "left", borderBottom: "1px solid var(--border)" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {report.results.map((r) => {
                  const m = statusMeta[r.status] || statusMeta.failed;
                  return (
                    <tr key={r.row} style={{ borderBottom: "1px solid var(--border)" }} data-testid={`report-row-${r.row}`}>
                      <td style={{ padding: "9px 14px", fontSize: 12, color: "var(--text-2)" }} className="font-mono-data">{r.row}</td>
                      <td style={{ padding: "9px 14px", fontSize: 13, fontWeight: 600 }}>{r.company || "—"}</td>
                      <td style={{ padding: "9px 14px", fontSize: 13 }}>
                        <span className="flex items-center gap-1" style={{ color: m.color }}>
                          <m.Icon size={14} /> {m.label}
                        </span>
                      </td>
                      <td style={{ padding: "9px 14px", fontSize: 12, color: "var(--text-2)" }}>{r.reason || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex gap-3 justify-end mt-6">
            {report.failed > 0 && (
              <button onClick={downloadFailed} className="btn-outline mr-auto" data-testid="report-download-failed">
                <Download size={15} /> Download failed rows
              </button>
            )}
            <button onClick={() => { setReport(null); }} className="btn-outline" data-testid="report-import-more">Import another file</button>
            <button onClick={onClose} className="btn-primary" data-testid="report-done">Done</button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title={title} subtitle={subtitle} onClose={onClose} size="lg">
      <div data-testid="import-modal">
        <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={onFile} style={{ display: "none" }} data-testid="import-file-input" />
        {!columns.length ? (
          <div
            onClick={() => fileRef.current?.click()}
            className="card-ledger"
            style={{ padding: 40, textAlign: "center", cursor: "pointer", background: "var(--surface-2)", borderStyle: "dashed" }}
            data-testid="import-dropzone"
          >
            <Upload size={28} style={{ color: "var(--primary)", margin: "0 auto 12px" }} />
            <div style={{ fontWeight: 600 }}>Choose a CSV file</div>
            <div style={{ fontSize: 12, color: "var(--text-2)", marginTop: 4 }}>First row must be column headers</div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <div style={{ fontSize: 13, color: "var(--text-2)" }}>{rowCount} data row(s) · {columns.length} columns</div>
              <button onClick={() => fileRef.current?.click()} className="btn-ghost" style={{ fontSize: 12, color: "var(--primary)" }}>Change file</button>
            </div>
            <div className="grid gap-3" style={{ gridTemplateColumns: "1fr 1fr" }}>
              {fields.map((f) => {
                if (f.key === "_blank") return <div key="_blank" />;
                return (
                <div key={f.key}>
                  <label className="overline" style={{ fontSize: 10, display: "block", marginBottom: 6 }}>
                    {f.label}{f.required ? <span style={{ color: "var(--primary)" }}> *</span> : null}
                  </label>
                  <select
                    value={mapping[f.key] || ""}
                    onChange={(e) => setField(f.key, e.target.value)}
                    className="input-base"
                    data-testid={`map-${f.key}`}
                  >
                    <option value="">— Not mapped —</option>
                    {columns.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                );
              })}
            </div>
          </>
        )}

        <div className="flex gap-3 justify-end mt-6">
          <button onClick={onClose} className="btn-outline">Cancel</button>
          <button onClick={doImport} disabled={!columns.length || importing} className="btn-primary" data-testid="import-commit">
            {importing ? <Loader2 size={15} className="spin" /> : <Upload size={15} />}
            {importing ? "Importing…" : "Import"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
